import React from 'react';
import { Box, Typography, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

// GST-compliant printable invoice. Props: clinic (Settings), invoice (persisted Invoice doc, populated patient+visit)
const PrintableInvoice = ({ clinic, invoice }) => {
  if (!invoice) return null;
  const patient = invoice.patient || {};
  const visit = invoice.visit || {};

  return (
    <Box
      className="invoice-print-sheet"
      sx={{ width: '210mm', minHeight: '148mm', bgcolor: '#fff', color: '#2b2740', p: '14mm', mx: 'auto', fontFamily: '"Inter", sans-serif' }}
    >
      {/* Letterhead */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #8B7FD6', pb: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#6B5FC4' }}>
            {clinic?.clinicName || 'EyeDealist Eye Clinic'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#948EA6', mt: 0.5 }}>
            {[clinic?.address?.line1, clinic?.address?.city, clinic?.address?.state, clinic?.address?.pincode].filter(Boolean).join(', ')}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#948EA6' }}>
            {clinic?.phone && `Phone: ${clinic.phone}`} {clinic?.email && ` · ${clinic.email}`}
          </Typography>
          {clinic?.gstin && (
            <Typography variant="caption" sx={{ display: 'block', color: '#948EA6' }}>GSTIN: {clinic.gstin}</Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#8B7FD6' }}>TAX INVOICE</Typography>
          <Typography variant="body2"><strong>Invoice No:</strong> {invoice.invoiceNumber}</Typography>
          <Typography variant="body2"><strong>Date:</strong> {new Date(invoice.createdAt).toLocaleDateString('en-IN')}</Typography>
        </Box>
      </Box>

      {/* Bill to */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">BILL TO</Typography>
          <Typography variant="body1" fontWeight={700}>{patient.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {patient.patientId} · {patient.age}y · {patient.gender} · {patient.contact?.phone}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">VISIT</Typography>
          <Typography variant="body2">{visit.visitId} {visit.tokenNumber ? `(Token #${visit.tokenNumber})` : ''}</Typography>
        </Box>
      </Box>

      {/* Items table */}
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#FAF9FC' }}>
            <TableCell><strong>#</strong></TableCell>
            <TableCell><strong>Description</strong></TableCell>
            <TableCell><strong>HSN/SAC</strong></TableCell>
            <TableCell align="center"><strong>Qty</strong></TableCell>
            <TableCell align="right"><strong>Rate (₹)</strong></TableCell>
            <TableCell align="right"><strong>GST %</strong></TableCell>
            <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoice.items?.map((item, i) => (
            <TableRow key={i}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{item.hsnSacCode || '-'}</TableCell>
              <TableCell align="center">{item.quantity}</TableCell>
              <TableCell align="right">{item.unitPrice?.toFixed(2)}</TableCell>
              <TableCell align="right">{item.gstRate || 0}%</TableCell>
              <TableCell align="right">{item.totalPrice?.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Box sx={{ width: '280px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">₹{invoice.subtotal?.toFixed(2)}</Typography>
          </Box>
          {invoice.discount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2">Discount</Typography>
              <Typography variant="body2">-₹{invoice.discount?.toFixed(2)}</Typography>
            </Box>
          )}
          {invoice.cgst > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2">CGST</Typography>
              <Typography variant="body2">₹{invoice.cgst?.toFixed(2)}</Typography>
            </Box>
          )}
          {invoice.sgst > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
              <Typography variant="body2">SGST</Typography>
              <Typography variant="body2">₹{invoice.sgst?.toFixed(2)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
            <Typography variant="subtitle1" fontWeight={700}>₹{invoice.totalAmount?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="success.main">Paid</Typography>
            <Typography variant="body2" color="success.main">₹{invoice.paidAmount?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="error.main" fontWeight={600}>Balance Due</Typography>
            <Typography variant="body2" color="error.main" fontWeight={600}>₹{invoice.balanceAmount?.toFixed(2)}</Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        {clinic?.receiptFooterNote || 'Thank you for choosing us for your eye care needs.'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
        This is a computer-generated invoice.
      </Typography>
    </Box>
  );
};

export default PrintableInvoice;
